import { IonButton, IonCard, IonIcon, IonImg, useIonModal } from "@ionic/react";
import {
  calendarOutline,
  gridOutline,
  hourglassOutline,
  repeatOutline,
  trashBinOutline,
  trashOutline
} from "ionicons/icons";
import { useEffect, useState } from "react";
import { subscribeToBot, useBotsStore } from "../stores/bots.store";
import { useUsersStore } from "../stores/users.store";
import BotAvatar from "./BotAvatar";
import ProfitIndicator from "./ProfitIndicator";
import UserName from "./UserName";
import { useExchangeRatesStore } from "../stores/exchangeRates.store";
import {
  addBotAction,
  createActionByType,
  deleteBot,
  updateBot
} from "../lib/firebase";
import { SetupBot } from "../pages/SetupBot";
import moment from "moment";
import GridItem from "./GridItem";
import GenericInfo from "../pages/GenericInfo";
import { ReactComponent as LobbiesIcon } from "../assets/graphics/workspaces.svg";
import { calculateProgress } from "../utils/botUtils";
import { useAuthStore } from "../stores/auth.store";
import SaveStrategyButton from "./SaveStrategyButton";
import { ReactComponent as TradesIcon } from "../assets/graphics/mode_heat.svg";
import { ReactComponent as StreaksIcon } from "../assets/graphics/mode_streaks.svg";

interface BotHeaderProps {
  botId: string;
}

const BotHeader: React.FC<BotHeaderProps> = ({ botId }) => {
  const uid = useAuthStore(state => state.user?.uid);
  const bot = useBotsStore(state => state.bots.find(b => b.id === botId));
  const [progress, setProgress] = useState(0);
  const user = useUsersStore(state =>
    state.users.find(u => u.userId === bot?.user)
  );
  // minus a few seconds to prevent it reading in a few seconds rather now
  const totalTimestamp = bot?.totalTimestamp && bot.totalTimestamp - 5;

  const exchangeRates = useExchangeRatesStore(
    (state: any) => state.exchangeRates
  );

  const [presentConfirm, dismissConfirm] = useIonModal(GenericInfo, {
    onDismiss: async (confirm: boolean) => {
      dismissConfirm();
      if (confirm && user?.userId && botId) {
        await updateBot(botId, {
          active: false
        });
      }
    },
    title: `Deactivate Bot?`,
    subtitle: `Are you sure you want to deactivate your ${bot?.name} bot? This will stop your bot from trading and move it to the Past list.`,
    buttonText: "Yes - Deactivate Bot",
    icon: trashBinOutline
  });

  const [presentSetupBot, dismissSetupBot] = useIonModal(SetupBot, {
    botId,
    mode: bot?.mode,
    onDismiss: () => dismissSetupBot(),
    backdropDismiss: false
  });

  const [presentDeleteConfirm, dismissDeleteConfirm] = useIonModal(
    GenericInfo,
    {
      onDismiss: async (confirm: boolean) => {
        dismissDeleteConfirm();
        if (confirm && user?.userId && botId) {
          await deleteBot(botId);
        }
      },
      title: "Delete Bot?",
      subtitle: `Are you sure you want to delete your ${bot?.name} bot? This will remove the data permanently.`,
      buttonText: "Yes - Delete Bot",
      icon: trashOutline
    }
  );

  useEffect(() => {
    if (!bot) {
      subscribeToBot(botId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerHeartbeat = async () => {
    addBotAction(botId, createActionByType("viewBot", user.userId))
      .then(() => console.log("Refresh signal sent."))
      .catch((error: any) => console.error("Data could not be saved." + error));
  };

  const editBot = () => {
    presentSetupBot();
  };

  useEffect(() => {
    // Only register live updates for your own bots
    if (uid && user?.userId === uid) {
      registerHeartbeat();
      const intervalId = setInterval(() => {
        registerHeartbeat();
      }, 60000); // Executes every minute

      // // Clear interval on component unmount
      return () => clearInterval(intervalId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, user?.userId]);

  useEffect(() => {
    if (totalTimestamp && bot?.mode === "backtest") {
      setProgress(
        calculateProgress(
          totalTimestamp,
          bot?.backtestConfig?.startTime,
          bot?.backtestConfig?.endTime
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalTimestamp]);

  const date = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  const getLatestTrade = (trades: { [key: string]: any }): any => {
    let latestTrade: any = null;
    let latestDate: number = 0;

    for (const tradeId in trades) {
      const trade = trades[tradeId];
      const buyDate = trade.buy?.dateClosed || 0;
      const sellDate = trade.sell?.dateClosed || 0;
      const tradeDate = Math.max(buyDate, sellDate);

      if (tradeDate > latestDate) {
        latestTrade = trade;
        latestDate = tradeDate;
      }
    }

    return latestTrade;
  };

  if (!bot) return null;
  const latestTrade = getLatestTrade(bot?.data?.trades);
  let latestTradeFormatDt;
  if (latestTrade?.buy?.dateClosed) {
    let dateClosed =
      bot?.mode === "live"
        ? latestTrade?.buy?.dateClosed
        : latestTrade?.buy?.dateClosed * 1000;
    latestTradeFormatDt = moment(dateClosed).fromNow();
  }
  const latestUpdate = moment(bot?.totalTimestamp * 1000 - 20).fromNow();

  const backtestDuration = moment
    .duration(
      moment(bot?.backtestConfig?.endTime).diff(
        moment(bot?.backtestConfig?.startTime)
      )
    )
    .humanize();

  const canDeactivate =
    bot?.user === uid && bot?.mode !== "backtest" && bot?.active && !bot?.lobby;
  const canEdit =
    bot?.user === uid && bot?.mode !== "backtest" && bot?.provisioned;
  const canDelete =
    bot?.user === uid &&
    bot?.mode === "backtest" &&
    bot?.deactivated &&
    !bot?.lobby;
  return (
    <>
      <IonCard className="rounded-lg shadow-lg bg-primary-60 max-w-md md:max-w-xl mx-auto">
        <div className="bg-primary-60 p-2 text-white flex items-center">
          <BotAvatar
            botId={botId}
            small
            progress={
              bot?.mode === "backtest" && bot?.deactivated ? 100 : progress
            }
          />
          <div className="grow px-2">
            <div className="text-xl font-semibold">{bot?.name}</div>
            <div className="flex items-center">
              <ProfitIndicator profit={bot?.profit} />
              <div className="flex text-white w-[32px] mx-2">
                <div className="bg-[#ff7f0a] opacity-70 p-[2px] rounded h-4 mt-[2px] mr-1">
                  <TradesIcon />
                </div>
                {bot?.tradeCount || 0}
              </div>
              <div className="text-white flex w-[44px] ">
                <StreaksIcon /> {bot?.profitStreak || 0}/
                {bot?.maxProfitStreak || 0}
              </div>
            </div>
            <UserName userId={bot?.user} />
          </div>
          <div className="flex flex-col">
            {canEdit && (
              <IonButton size="small" color="primary" onClick={editBot}>
                Edit
              </IonButton>
            )}
            {canDeactivate && (
              <IonButton
                size="small"
                color="primary"
                onClick={() => presentConfirm()}
              >
                <IonIcon icon={trashOutline} slot="icon-only" />
              </IonButton>
            )}
            {bot?.lobby && (
              <IonButton
                size="small"
                color="primary"
                routerLink={`/lobbies/${bot.lobby}`}
              >
                <LobbiesIcon className="p-1 selected-fill" />
              </IonButton>
            )}
            {canDelete && (
              <IonButton
                size="small"
                color="danger"
                onClick={() => presentDeleteConfirm()}
              >
                <IonIcon icon={trashOutline} slot="icon-only" />
              </IonButton>
            )}
          </div>
        </div>
        <div className="bg-primary-70 p-2 pl-4 text-white flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <IonImg
              className="w-8 h-8 rounded-full mr-2"
              src={`https://raw.githubusercontent.com/cjdowner/cryptocurrency-icons/master/128/icon/${bot?.quoteAsset?.toLowerCase()}.png`}
            />
            {bot?.totalAsset?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }) || 0}
            <span className="pl-2 text-xs">{latestUpdate}</span>
            {bot?.totalAsset && exchangeRates["USDT/ZAR"] && (
              <span className="pl-2">
                (R
                {" " +
                  (bot?.totalAsset * exchangeRates["USDT/ZAR"])?.toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }
                  )}
                )
              </span>
            )}
          </div>
          <SaveStrategyButton bot={bot} />
        </div>
        {bot?.provisioned && (
          <div className="bg-primary-80 p-2 text-white text-xsm">
            {bot?.mode !== "backtest" && (
              <>
                <GridItem
                  icon={calendarOutline}
                  title="Last Trade"
                  value={latestTradeFormatDt || "N/A"}
                />
                {bot?.active && (
                  <GridItem
                    icon={gridOutline}
                    title="Trade Slots Available"
                    value={
                      bot?.data?.portfolio?.openTradeSlots > 0
                        ? bot?.data?.portfolio?.openTradeSlots
                        : 0
                    }
                  />
                )}
              </>
            )}
            {bot?.mode === "backtest" && (
              <>
                <GridItem
                  icon={hourglassOutline}
                  title="Progress"
                  value={`${progress}%`}
                />
                <GridItem
                  icon={calendarOutline}
                  title="Duration"
                  value={backtestDuration}
                />
              </>
            )}
            <GridItem
              icon={calendarOutline}
              title="Started"
              value={date(bot?.startedTime)}
            />
            <GridItem
              icon={repeatOutline}
              title="Trades"
              value={bot?.tradeCount || 0}
            />
          </div>
        )}
      </IonCard>
    </>
  );
};

export default BotHeader;
